import React, { useState, useEffect, useCallback } from 'react';
import { FaUserPlus, FaEdit, FaTrash, FaEye, FaSort, FaFilter, FaTimes, FaFileDownload, FaFileUpload } from 'react-icons/fa';
import { db, auth } from '../firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, where, orderBy, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';

const Students = () => {
  const navigate = useNavigate();
  const [isAdminOrManager, setIsAdminOrManager] = useState(false);
  const [roleChecked, setRoleChecked] = useState(false);
  const [students, setStudents] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sortField, setSortField] = useState('firstName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filters, setFilters] = useState({
    grade: '',
    stream: '',
    school: '',
    search: ''
  });
  const [isFiltering, setIsFiltering] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    studentId: '',
    parentId: '',
    grade: '',
    stream: '',
    school: '',
    gender: '',
    dateOfBirth: '',
    academicHistory: []
  });

  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');

  const checkAuthAndRole = useCallback(async () => {
    const user = auth.currentUser;
    
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        navigate('/dashboard');
        return;
      }
      
      const userRole = userDoc.data().role;
      
      // Allow admins, managers, drivers, and assistants to access
      const allowedRoles = ['administrator', 'manager', 'driver', 'assistant'];
      if (!allowedRoles.includes(userRole)) {
        navigate('/dashboard');
        return;
      }
      
      setIsAdminOrManager(userRole === 'administrator' || userRole === 'manager');
      setRoleChecked(true);
    } catch (err) {
      console.error('Error checking user role:', err);
      navigate('/login');
    }
  }, [navigate]);

  const fetchStudents = useCallback(async () => {
    try {
      setIsFiltering(true);
      setError('');
      let q = collection(db, 'students');
      
      // Apply filters
      if (filters.grade) {
        q = query(q, where('currentGrade', '==', filters.grade));
      }
      
      // Apply sorting
      q = query(q, orderBy(sortField, sortDirection));

      const querySnapshot = await getDocs(q);
      let studentsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Apply text-based filters (stream, school) with partial matching
      if (filters.stream) {
        const streamLower = filters.stream.toLowerCase();
        studentsList = studentsList.filter(student => 
          (student.currentStream || '').toLowerCase().includes(streamLower)
        );
      }
      
      if (filters.school) {
        const schoolLower = filters.school.toLowerCase();
        studentsList = studentsList.filter(student => 
          (student.currentSchool || '').toLowerCase().includes(schoolLower)
        );
      }

      // Apply search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        studentsList = studentsList.filter(student => 
          (student.firstName || '').toLowerCase().includes(searchLower) ||
          (student.lastName || '').toLowerCase().includes(searchLower) ||
          (student.studentId || '').toLowerCase().includes(searchLower) ||
          (student.parentId || '').toLowerCase().includes(searchLower)
        );
      }

      setStudents(studentsList);
    } catch (err) {
      console.error('Error fetching students:', err);
      setError('Failed to fetch students. Please try again.');
    } finally {
      setIsFiltering(false);
    }
  }, [sortField, sortDirection, filters]);

  useEffect(() => {
    checkAuthAndRole();
  }, [checkAuthAndRole]);

  useEffect(() => {
    // Only fetch students after role is checked and confirmed
    if (roleChecked) {
      fetchStudents();
    }
  }, [roleChecked, fetchStudents]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    // Add a small delay to prevent too many rapid filter changes
    setTimeout(() => {
      fetchStudents();
    }, 300);
  };

  const clearFilters = () => {
    setFilters({
      grade: '',
      stream: '',
      school: '',
      search: ''
    });
  };

  const handleViewStudent = (student) => {
    setSelectedStudent(student);
    setShowViewModal(true);
  };

  const handleEditStudent = (student) => {
    setSelectedStudent(student);
    setFormData({
      ...student
    });
    setShowEditModal(true);
  };

  const handleUpdateStudent = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const studentRef = doc(db, 'students', selectedStudent.id);
      const currentData = selectedStudent;

      // Check if academic details have changed
      const academicChanged = 
        currentData.currentGrade !== formData.grade ||
        currentData.currentStream !== formData.stream ||
        currentData.currentSchool !== formData.school;

      // Update the end date of the previous academic record if it exists
      const academicHistory = [...currentData.academicHistory];
      if (academicHistory.length > 0) {
        const lastRecord = academicHistory[academicHistory.length - 1];
        if (lastRecord.endDate === null) {
          lastRecord.endDate = new Date().toISOString();
        }
      }

      // Add new academic record if details have changed
      if (academicChanged) {
        academicHistory.push({
          grade: formData.grade,
          stream: formData.stream,
          school: formData.school,
          startDate: new Date().toISOString(),
          endDate: null
        });
      }

      const updateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        studentId: formData.studentId,
        parentId: formData.parentId,
        gender: formData.gender,
        dateOfBirth: formData.dateOfBirth,
        academicHistory: academicHistory,
        currentGrade: formData.grade,
        currentStream: formData.stream,
        currentSchool: formData.school,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(studentRef, updateData);
      setSuccess('Student updated successfully!');
      setShowEditModal(false);
      fetchStudents();
    } catch (err) {
      console.error('Error updating student:', err);
      setError(err.message || 'Failed to update student');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (window.confirm('Are you sure you want to delete this student?')) {
      try {
        await deleteDoc(doc(db, 'students', studentId));
        setSuccess('Student deleted successfully');
        fetchStudents();
      } catch (err) {
        console.error('Error deleting student:', err);
        setError('Failed to delete student');
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Check if student already exists
      const studentQuery = query(
        collection(db, 'students'),
        where('studentId', '==', formData.studentId)
      );
      const querySnapshot = await getDocs(studentQuery);

      if (!querySnapshot.empty) {
        setError('A student with this ID already exists');
        setLoading(false);
        return;
      }

      const studentData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        studentId: formData.studentId,
        parentId: formData.parentId,
        gender: formData.gender,
        dateOfBirth: formData.dateOfBirth,
        academicHistory: [{
          grade: formData.grade,
          stream: formData.stream,
          school: formData.school,
          startDate: new Date().toISOString(),
          endDate: null // null indicates current
        }],
        currentGrade: formData.grade,
        currentStream: formData.stream,
        currentSchool: formData.school,
        createdAt: new Date().toISOString()
      };

      await addDoc(collection(db, 'students'), studentData);
      setSuccess('Student added successfully!');
      setShowAddForm(false);
      setFormData({
        firstName: '',
        lastName: '',
        studentId: '',
        parentId: '',
        grade: '',
        stream: '',
        school: '',
        gender: '',
        dateOfBirth: '',
        academicHistory: []
      });
      fetchStudents();
    } catch (err) {
      console.error('Error adding student:', err);
      setError(err.message || 'Failed to add student. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const headers = [
      'firstName',
      'lastName',
      'studentId',
      'parentId',
      'currentGrade',
      'currentStream',
      'currentSchool',
      'gender',
      'dateOfBirth'
    ];
    
    const csv = Papa.unparse({
      fields: headers,
      data: []
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'students_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setImportFile(file);
      setImportError('');
    } else {
      setImportError('Please select a valid CSV file');
      setImportFile(null);
    }
  };

  const validateStudentData = (data) => {
    const requiredFields = ['firstName', 'lastName', 'studentId', 'parentId', 'currentGrade', 'currentStream', 'currentSchool', 'gender', 'dateOfBirth'];
    const errors = [];

    requiredFields.forEach(field => {
      if (!data[field]) {
        errors.push(`Missing required field: ${field}`);
      }
    });

    if (data.currentGrade) {
      const validGrades = ['0000', '000', '00', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
      if (!validGrades.includes(data.currentGrade.toString())) {
        errors.push('Grade must be one of: 0000, 000, 00, 0, 1-12');
      }
    }

    if (data.gender && !['male', 'female', 'other'].includes(data.gender.toLowerCase())) {
      errors.push('Gender must be male, female, or other');
    }

    return errors;
  };

  const handleImport = async () => {
    if (!importFile) {
      setImportError('Please select a file to import');
      return;
    }

    setImportProgress(0);
    setImportError('');
    setImportSuccess('');
    setLoading(true);

    try {
      const text = await importFile.text();
      const { data, errors } = Papa.parse(text, {
        header: true,
        skipEmptyLines: true
      });

      if (errors.length > 0) {
        setImportError('Error parsing CSV file: ' + errors[0].message);
        return;
      }

      const validationErrors = [];
      const validStudents = [];

      data.forEach((row, index) => {
        const errors = validateStudentData(row);
        if (errors.length > 0) {
          validationErrors.push(`Row ${index + 2}: ${errors.join(', ')}`);
        } else {
          // Create student data with academic history
          validStudents.push({
            firstName: row.firstName,
            lastName: row.lastName,
            studentId: row.studentId,
            parentId: row.parentId,
            gender: row.gender,
            dateOfBirth: row.dateOfBirth,
            currentGrade: row.currentGrade,
            currentStream: row.currentStream,
            currentSchool: row.currentSchool,
            academicHistory: [{
              grade: row.currentGrade,
              stream: row.currentStream,
              school: row.currentSchool,
              startDate: new Date().toISOString(),
              endDate: null
            }],
            createdAt: new Date().toISOString()
          });
        }
      });

      if (validationErrors.length > 0) {
        setImportError('Validation errors:\n' + validationErrors.join('\n'));
        return;
      }

      // Upload valid students to Firebase
      const batchSize = 50;
      const totalBatches = Math.ceil(validStudents.length / batchSize);

      for (let i = 0; i < totalBatches; i++) {
        const batch = validStudents.slice(i * batchSize, (i + 1) * batchSize);
        await Promise.all(batch.map(student => addDoc(collection(db, 'students'), student)));
        setImportProgress(((i + 1) / totalBatches) * 100);
      }

      setImportSuccess(`Successfully imported ${validStudents.length} students`);
      setShowImportModal(false);
      fetchStudents();
    } catch (err) {
      console.error('Error importing students:', err);
      setImportError('Failed to import students: ' + err.message);
    } finally {
      setLoading(false);
      setImportFile(null);
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(students.length / pageSize);
  const paginatedStudents = students.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Show loading while checking role
  if (!roleChecked) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00E0E0] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700">Loading...</h2>
          <p className="text-gray-500 mt-2">Please wait while we verify your access</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00E0E0] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700">Loading Students...</h2>
          <p className="text-gray-500 mt-2">Please wait while we fetch your student data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-black">Students Management</h1>
          <div className="flex space-x-4">
            <button
              onClick={downloadTemplate}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
            >
              <FaFileDownload />
              <span>Download Template</span>
            </button>
            {isAdminOrManager && (
              <button
                onClick={() => setShowImportModal(true)}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
              >
                <FaFileUpload />
                <span>Import CSV</span>
              </button>
            )}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
            >
              <FaFilter />
              <span>Filters</span>
            </button>
            {isAdminOrManager && (
              <button
                onClick={() => setShowAddForm(true)}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#00E0E0] text-white font-semibold hover:bg-[#00B8B8] transition-colors"
              >
                <FaUserPlus />
                <span>Add New Student</span>
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-500/10 border border-green-500 text-green-500 px-4 py-3 rounded-lg mb-6">
            {success}
          </div>
        )}

        {/* Filters */}
        {showFilters && (
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Search students..."
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Grade</label>
                <select
                  name="grade"
                  value={filters.grade}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                >
                  <option value="">All Grades</option>
                  <option value="0000">Grade 0000</option>
                  <option value="000">Grade 000</option>
                  <option value="00">Grade 00</option>
                  <option value="0">Grade 0</option>
                  {[...Array(12)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>Grade {i + 1}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Stream</label>
                <input
                  type="text"
                  name="stream"
                  value={filters.stream}
                  onChange={handleFilterChange}
                  placeholder="Enter stream name"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">School</label>
                <input
                  type="text"
                  name="school"
                  value={filters.school}
                  onChange={handleFilterChange}
                  placeholder="Enter school name"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                />
              </div>
              <div className="col-span-4 flex justify-end items-center space-x-4">
                {isFiltering && (
                  <div className="text-sm text-gray-500">
                    <span className="animate-pulse">Filtering...</span>
                  </div>
                )}
                <button
                  onClick={clearFilters}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                >
                  <FaTimes />
                  <span>Clear Filters</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Students List Controls */}
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-gray-700 font-medium">Showing {paginatedStudents.length} of {students.length} students</div>
          <div className="flex items-center space-x-2">
            <label htmlFor="pageSize" className="text-sm text-gray-700">Rows per page:</label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              className="px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0] bg-white"
              style={{ minWidth: '80px' }}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {/* Students List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('firstName')}>
                  <div className="flex items-center space-x-1">
                    <span>Student</span>
                    <FaSort className={`text-gray-400 ${sortField === 'firstName' ? 'text-gray-600' : ''}`} />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('studentId')}>
                  <div className="flex items-center space-x-1">
                    <span>Student ID</span>
                    <FaSort className={`text-gray-400 ${sortField === 'studentId' ? 'text-gray-600' : ''}`} />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('currentGrade')}>
                  <div className="flex items-center space-x-1">
                    <span>Current Grade</span>
                    <FaSort className={`text-gray-400 ${sortField === 'currentGrade' ? 'text-gray-600' : ''}`} />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('currentStream')}>
                  <div className="flex items-center space-x-1">
                    <span>Current Stream</span>
                    <FaSort className={`text-gray-400 ${sortField === 'currentStream' ? 'text-gray-600' : ''}`} />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('currentSchool')}>
                  <div className="flex items-center space-x-1">
                    <span>Current School</span>
                    <FaSort className={`text-gray-400 ${sortField === 'currentSchool' ? 'text-gray-600' : ''}`} />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedStudents.map((student) => (
                <tr key={student.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {student.firstName} {student.lastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {student.parentId}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {student.studentId}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    Grade {student.currentGrade}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {student.currentStream}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {student.currentSchool}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleViewStudent(student)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Details"
                      >
                        <FaEye />
                      </button>
                      {isAdminOrManager && (
                        <>
                          <button
                            onClick={() => handleEditStudent(student)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Edit Student"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDeleteStudent(student.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Student"
                          >
                            <FaTrash />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="flex justify-end items-center mt-4 space-x-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Next
          </button>
        </div>

        {/* View Student Modal */}
        {showViewModal && selectedStudent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-black">Student Details</h2>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ×
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-[#00E0E0] mb-4">Personal Information</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Name</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedStudent.firstName} {selectedStudent.lastName}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Gender</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedStudent.gender}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedStudent.dateOfBirth}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-[#00E0E0] mb-4">Academic Information</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Student ID</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedStudent.studentId}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Parent ID</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedStudent.parentId}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Grade</label>
                        <p className="mt-1 text-sm text-gray-900">Grade {selectedStudent.currentGrade}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Stream</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedStudent.currentStream}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">School</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedStudent.currentSchool}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-[#00E0E0] mb-4">Academic History</h3>
                  <div className="space-y-4">
                    {selectedStudent.academicHistory?.map((record, index) => (
                      <div key={index} className="border-l-2 border-[#00E0E0] pl-4">
                        <div className="text-sm text-gray-600">
                          {new Date(record.startDate).toLocaleDateString()} - {record.endDate ? new Date(record.endDate).toLocaleDateString() : 'Present'}
                        </div>
                        <div className="text-sm font-medium">
                          Grade {record.grade} - {record.stream} at {record.school}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Student Modal */}
        {(showAddForm || showEditModal) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-black">
                    {showAddForm ? 'Add New Student' : 'Edit Student'}
                  </h2>
                  <button
                    onClick={() => showAddForm ? setShowAddForm(false) : setShowEditModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={showAddForm ? handleSubmit : handleUpdateStudent} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Personal Information */}
                    <div className="space-y-6">
                      <h3 className="text-xl font-semibold text-[#00E0E0]">Personal Information</h3>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                        <input
                          type="text"
                          name="firstName"
                          required
                          value={formData.firstName}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                        <input
                          type="text"
                          name="lastName"
                          required
                          value={formData.lastName}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Gender *</label>
                        <select
                          name="gender"
                          required
                          value={formData.gender}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        >
                          <option value="">Select Gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth *</label>
                        <input
                          type="date"
                          name="dateOfBirth"
                          required
                          value={formData.dateOfBirth}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>
                    </div>

                    {/* Academic Information */}
                    <div className="space-y-6">
                      <h3 className="text-xl font-semibold text-[#00E0E0]">Academic Information</h3>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Student ID *</label>
                        <input
                          type="text"
                          name="studentId"
                          required
                          value={formData.studentId}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Parent ID *</label>
                        <input
                          type="text"
                          name="parentId"
                          required
                          value={formData.parentId}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Grade *</label>
                        <select
                          name="grade"
                          required
                          value={formData.grade}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        >
                          <option value="">Select Grade</option>
                          <option value="0000">Grade 0000</option>
                          <option value="000">Grade 000</option>
                          <option value="00">Grade 00</option>
                          <option value="0">Grade 0</option>
                          {[...Array(12)].map((_, i) => (
                            <option key={i + 1} value={i + 1}>Grade {i + 1}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Stream *</label>
                        <input
                          type="text"
                          name="stream"
                          required
                          value={formData.stream}
                          onChange={handleInputChange}
                          placeholder="Enter stream name"
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">School *</label>
                        <input
                          type="text"
                          name="school"
                          required
                          value={formData.school}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-4">
                    <button
                      type="button"
                      onClick={() => showAddForm ? setShowAddForm(false) : setShowEditModal(false)}
                      className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className={`flex items-center space-x-2 px-6 py-3 rounded-lg bg-[#00E0E0] text-white font-semibold hover:bg-[#00B8B8] transition-colors ${
                        loading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {showAddForm ? <FaUserPlus /> : <FaEdit />}
                      <span>{loading ? (showAddForm ? 'Adding...' : 'Updating...') : (showAddForm ? 'Add Student' : 'Update Student')}</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Import Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-black">Import Students</h2>
                  <button
                    onClick={() => setShowImportModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ×
                  </button>
                </div>

                <div className="space-y-6">
                  <div>
                    <p className="text-gray-600 mb-4">
                      Upload a CSV file containing student information. Make sure your file matches the template format.
                    </p>
                    <button
                      onClick={downloadTemplate}
                      className="text-[#00E0E0] hover:text-[#00B8B8] font-medium"
                    >
                      Download Template
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select CSV File
                    </label>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileSelect}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                    />
                  </div>

                  {importError && (
                    <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg">
                      {importError}
                    </div>
                  )}

                  {importSuccess && (
                    <div className="bg-green-500/10 border border-green-500 text-green-500 px-4 py-3 rounded-lg">
                      {importSuccess}
                    </div>
                  )}

                  {importProgress > 0 && (
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-[#00E0E0] h-2.5 rounded-full"
                        style={{ width: `${importProgress}%` }}
                      ></div>
                    </div>
                  )}

                  <div className="flex justify-end space-x-4">
                    <button
                      onClick={() => setShowImportModal(false)}
                      className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleImport}
                      disabled={!importFile || loading}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#00E0E0] text-white font-semibold hover:bg-[#00B8B8] transition-colors ${
                        (!importFile || loading) ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <FaFileUpload />
                      <span>{loading ? 'Importing...' : 'Import'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Students; 